class NewQuickView extends HTMLElement {
  constructor() {
    super();
    this.modalOverlay = null;
    this.modalContainer = null;
    this.currentProductUrl = "";
    this.translations = {};

    // Bind methods to preserve 'this' context
    this.handleQuickViewClick = this.handleQuickViewClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.init = this.init.bind(this);
  }

  connectedCallback() {
    // Charger les traductions depuis l'attribut data
    try {
      const translationsData = this.getAttribute('data-translations');
      if (translationsData) {
        this.translations = JSON.parse(translationsData);
      }
    } catch (error) {
      console.error('Error parsing translations:', error);
    }

    // Utiliser la délégation d'événements pour gérer les clics sur les éléments avec l'attribut new-quick-view
    document.body.addEventListener("click", this.handleDocumentClick);

    // Initialiser le script
    this.init();

    // Réinitialiser en cas de chargement dynamique de contenu
    document.addEventListener("shopify:section:load", this.init);

    // Exposer la fonction init pour permettre la réinitialisation depuis d'autres scripts
    window.newQuickViewInit = this.init;

    // Réinitialiser après l'initialisation des carrousels
    setTimeout(() => {
      // Réinitialiser après un délai pour s'assurer que tous les carrousels sont chargés
      this.init();
    }, 1000);
  }

  // Helper method to get translation
  t(key) {
    return this.translations[key] || key;
  }

  handleDocumentClick(event) {
    this.handleQuickViewClick(event, this);
  }

  handleQuickViewClick(event, component) {
    // Trouver l'élément cliqué ou son parent le plus proche avec l'attribut new-quick-view
    let target = event.target;
    while (target && target !== document.body) {
      if (target.hasAttribute("new-quick-view")) {
        // Empêcher le comportement par défaut
        event.preventDefault();
        event.stopPropagation();

        // Récupérer l'URL du produit
        const productUrl = target.getAttribute("data-product-url");

        // Détecter le préfixe de langue actif (/en, /fr, etc.)
        const langPrefixMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
        const langPrefix = langPrefixMatch ? `/${langPrefixMatch[1]}` : '';

        // Normaliser l'URL du produit (relative, sans domaine ni langue)
        const productPath = new URL(productUrl, window.location.origin)
          .pathname
          .replace(/^\/[a-z]{2}(?=\/products\/)/, '');

        // Construire l'URL finale avec la langue active
        const url = new URL(`${langPrefix}${productPath}`, window.location.origin);
        url.searchParams.set('locale', this.getLocale());
        component.openModal(url);
        return;
      }
      target = target.parentElement;
    }
  }

  handleEscapeKey(event) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }

  // Fonction pour fermer la modal
  closeModal() {
    if (this.modalOverlay) {
      this.modalOverlay.classList.remove("active");
      document.body.classList.remove("new-quick-view-open");
    }
  }

  getLocale() {
    // 1. source la plus fiable : <html lang="en">
    const htmlLang = document.documentElement.lang;
    if (htmlLang && htmlLang.length === 2) return htmlLang;

    // 2. Shopify JS
    if (window.Shopify?.locale) return window.Shopify.locale;

    // 3. Détection URL propre (/en/, /fr/)
    const match = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    if (match) return match[1];

    // 4. fallback
    return 'en';
  }

  // Fonction pour ouvrir la modal
  openModal(productUrl) {
    if (this.modalOverlay) {
      // Afficher l'overlay
      this.modalOverlay.classList.add("active");
      document.body.classList.add("new-quick-view-open");

      // Réinitialiser le contenu de la modal
      this.resetModalContent();

      // Afficher l'état de chargement
      const loadingElement = this.modalContainer.querySelector(
        ".new-quick-view-loading",
      );
      if (loadingElement) {
        loadingElement.style.display = "flex";
      }

      // Charger les données du produit
      if (productUrl) {
        this.currentProductUrl = productUrl;
        this.loadProductData(productUrl);
      }
    }
  }

  // Fonction pour réinitialiser le contenu de la modal
  resetModalContent() {
    // Réinitialiser le fil d'ariane
    const breadcrumbElement = this.modalContainer.querySelector(
      "[data-product-breadcrumb]",
    );
    if (breadcrumbElement) {
      breadcrumbElement.innerHTML = "";
    }

    // Réinitialiser le titre
    const titleElement = this.modalContainer.querySelector("[data-product-title]");
    if (titleElement) {
      titleElement.textContent = "";
    }

    // Réinitialiser le sous-titre
    const subtitleElement = this.modalContainer.querySelector(
      "[data-product-subtitle]",
    );
    if (subtitleElement) {
      subtitleElement.innerHTML = "";
      subtitleElement.style.display = "none";
    }

    // Réinitialiser les avis JudgeMe
    const judgemeElement = this.modalContainer.querySelector(
      "[data-product-judgeme]",
    );
    if (judgemeElement) {
      judgemeElement.innerHTML = "";
      judgemeElement.style.display = "none";
    }

    // Réinitialiser le lecteur audio
    const audioPlayerElement = this.modalContainer.querySelector(
      "[data-product-audio-player]",
    );
    if (audioPlayerElement) {
      audioPlayerElement.innerHTML = "";
      audioPlayerElement.style.display = "none";
    }

    // Réinitialiser l'image
    const imageElement = this.modalContainer.querySelector("[data-main-image]");
    if (imageElement) {
      imageElement.src = "";
      imageElement.alt = "";
    }

    // Réinitialiser le prix dans le bouton
    const priceButtonElement = this.modalContainer.querySelector(
      "[data-product-price-button]",
    );
    if (priceButtonElement) {
      priceButtonElement.innerHTML = "";
    }

    // Réinitialiser la description
    const descriptionElement = this.modalContainer.querySelector(
      "[data-product-description]",
    );
    if (descriptionElement) {
      descriptionElement.innerHTML = "";
    }

    // Réinitialiser le lien du produit
    const productLinkElement =
      this.modalContainer.querySelector("[data-product-url]");
    if (productLinkElement) {
      productLinkElement.href = "";
    }

    // Réinitialiser les variantes
    const variantsContainer = this.modalContainer.querySelector(
      "[data-product-variants]",
    );
    if (variantsContainer) {
      variantsContainer.innerHTML = "";
    }

    // Réinitialiser le champ de gravure
    const gravureContainer = this.modalContainer.querySelector(
      ".gravure-input-container",
    );
    if (gravureContainer) {
      gravureContainer.remove();
    }
  }

  // Fonction pour charger les données du produit
  loadProductData(productUrl) {
    try {
      // Détecter le préfixe de langue actif (/en, /fr, etc.)
      const langPrefixMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
      const langPrefix = langPrefixMatch ? `/${langPrefixMatch[1]}` : '';

      // Normaliser l'URL du produit (relative, sans domaine ni langue)
      const productPath = new URL(productUrl, window.location.origin)
        .pathname
        .replace(/^\/[a-z]{2}(?=\/products\/)/, '');

      // Construire l'URL finale avec la langue active
      const url = new URL(`${langPrefix}${productPath}`, window.location.origin);
      url.searchParams.set('view', 'new-quick-view');
      url.searchParams.set('locale', this.getLocale());

      // Extraire le handle du produit (productId)
      const productId = productPath
        .split('/products/')[1]
        ?.split(/[?#/]/)[0];

      // Charger la vue quick view
      fetch(url.toString())
        .then((response) => {
          if (!response.ok) throw new Error('HTTP error');
          return response.text();
        })
        .then((html) => {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          // Mettre à jour le contenu de la modal
          this.updateModalContent(tempDiv);

          // Mettre à jour les avis Judge.me
          if (productId) {
            this.updateJudgeMeReviews(productUrl, productId, tempDiv);
          }

          // Masquer le loader
          const loadingElement = this.modalContainer.querySelector('.new-quick-view-loading');
          if (loadingElement) {
            loadingElement.style.display = 'none';
          }
        })
        .catch((error) => {
          console.error('Erreur lors du chargement du produit :', error);
          this.showQuickViewError();
        });
    } catch (error) {
      console.error('Erreur URL produit :', error);
      this.showQuickViewError();
    }
  }


  // Fonction pour mettre à jour les avis JudgeMe
  updateJudgeMeReviews(productUrl, productId, productData) {
    // Récupérer l'élément JudgeMe dans la modal
    const judgemeElement = this.modalContainer.querySelector(
      "[data-product-judgeme]",
    );
    if (!judgemeElement) return;

    // Créer un élément de badge JudgeMe personnalisé avec le format exact attendu par JudgeMe

    const productCard = document.querySelector(`[data-product-handle="${productId}"]`);
    const badgeElement = productCard?.querySelector('.jdgm-widget');

    if (judgemeElement && badgeElement) {
      judgemeElement.innerHTML = badgeElement.innerHTML;
      judgemeElement.style.display = "block";
    }

    // Forcer la réinitialisation de JudgeMe
    if (typeof window.jdgm !== "undefined") {
      // Si JudgeMe est déjà chargé, réinitialiser les badges
      if (window.jdgm.widgets && window.jdgm.widgets.preview_badge) {
        // Supprimer la classe jdgm--done-setup pour forcer la réinitialisation
        const badgeElement = judgemeElement.querySelector(".jdgm-widget");
        if (badgeElement) {
          badgeElement.classList.remove("jdgm--done-setup");
        }

        // Réinitialiser les badges
        setTimeout(() => {
          window.jdgm.widgets.preview_badge.setupWidgets(
            document.querySelectorAll(".jdgm-widget:not(.jdgm--done-setup)"),
          );
        }, 300);
      }
    } else {
      // Si JudgeMe n'est pas encore chargé, ajouter un écouteur pour l'événement jdgm.ready
      document.addEventListener("jdgm.ready", () => {
        setTimeout(() => {
          if (
            window.jdgm &&
            window.jdgm.widgets &&
            window.jdgm.widgets.preview_badge
          ) {
            const badgeElement = judgemeElement.querySelector(".jdgm-widget");
            if (badgeElement) {
              badgeElement.classList.remove("jdgm--done-setup");
            }
            window.jdgm.widgets.preview_badge.setupWidgets(
              document.querySelectorAll(".jdgm-widget:not(.jdgm--done-setup)"),
            );
          }
        }, 300);
      });
    }
  }

  // Fonction pour mettre à jour le contenu de la modal avec les données du produit
  updateModalContent(productData) {
    // Mettre à jour le fil d'ariane
    const breadcrumbElement = this.modalContainer.querySelector(
      "[data-product-breadcrumb]",
    );
    const productBreadcrumb = productData.querySelector(".product__breadcrumb");
    if (breadcrumbElement && productBreadcrumb) {
      breadcrumbElement.innerHTML = productBreadcrumb.innerHTML;
    }

    // Mettre à jour le titre du produit
    const titleElement = this.modalContainer.querySelector("[data-product-title]");
    const productTitle = productData.querySelector(".product-title");
    if (titleElement && productTitle) {
      titleElement.textContent = productTitle.textContent.trim();
    }

    // Mettre à jour le sous-titre du produit
    const subtitleElement = this.modalContainer.querySelector(
      "[data-product-subtitle]",
    );
    const productSubtitle = productData.querySelector(".product__subtitle");
    if (subtitleElement && productSubtitle) {
      subtitleElement.innerHTML = productSubtitle.innerHTML;
      subtitleElement.style.display = "block";
    } else if (subtitleElement) {
      subtitleElement.style.display = "none";
    }

    // Mettre à jour les avis JudgeMe
    const judgemeElement = this.modalContainer.querySelector(
      "[data-product-judgeme]",
    );
    const productJudgeme = productData.querySelector(".product__judgeme");
    if (judgemeElement && productJudgeme) {
      judgemeElement.innerHTML = productJudgeme.innerHTML;
      judgemeElement.style.display = "block";
    } else if (judgemeElement) {
      judgemeElement.style.display = "none";
    }

    // Mettre à jour l'image du produit
    const imageElement = this.modalContainer.querySelector("[data-main-image]");
    const productImage = productData.querySelector(".product__media-item img");
    if (imageElement && productImage) {
      imageElement.src = productImage.src;
      imageElement.alt = productImage.alt || "";
    }

    // Mettre à jour le lecteur audio
    const audioPlayerElement = this.modalContainer.querySelector(
      "[data-product-audio-player]",
    );
    const productAudioPlayer = productData.querySelector(
      ".product__audio-player",
    );
    if (audioPlayerElement && productAudioPlayer) {
      audioPlayerElement.innerHTML = productAudioPlayer.innerHTML;
      audioPlayerElement.style.display = "block";

      // Initialiser le lecteur audio
      this.initAudioPlayer();
    } else if (audioPlayerElement) {
      audioPlayerElement.style.display = "none";
    }

    // Mettre à jour le prix du produit dans le bouton
    const priceButtonElement = this.modalContainer.querySelector(
      "[data-product-price-button]",
    );
    const productPrice = productData.querySelector(".product__price");
    if (priceButtonElement && productPrice) {
      // Créer une structure de prix similaire à celle du formulaire de produit
      let priceHtml = "";
      const comparePrice = productPrice.querySelector(
        ".product__price--compare",
      );
      const actualPrice = productPrice.querySelector(".product__price--actual");

      if (comparePrice) {
        priceHtml += `<s class="btn__compare-price">${comparePrice.innerHTML}</s>`;
      }

      if (actualPrice) {
        priceHtml += `<span class="btn__current-price">${actualPrice.innerHTML}</span>`;
      }

      priceButtonElement.innerHTML = priceHtml;
    }

    // Mettre à jour la description du produit
    const descriptionElement = this.modalContainer.querySelector(
      "[data-product-description]",
    );
    const productDescription = productData.querySelector(
      ".product__description",
    );
    if (descriptionElement && productDescription) {
      descriptionElement.innerHTML = productDescription.innerHTML;
    }

    // Mettre à jour le lien "Voir le produit"
    const productLinkElement =
      this.modalContainer.querySelector("[data-product-url]");
    if (productLinkElement && this.currentProductUrl) {
      productLinkElement.href = this.currentProductUrl;
    }

    const productInputId = this.modalContainer.querySelector("input[name='id']");
    productInputId.value = productData.querySelector("input[name='id']").value;

    // Setup add to cart event listener - MOVED HERE so it always gets attached
    this.setupAddToCartHandler(productData);

    // Mettre à jour les variantes (si disponibles)
    const variantsContainer = this.modalContainer.querySelector(
      "[data-product-variants]",
    );
    const productVariants = productData.querySelector(".product__variants");
    if (variantsContainer && productVariants) {
      variantsContainer.innerHTML = productVariants.innerHTML;

      // Extraire l'ID de variante depuis les données JSON du produit
      try {
        // Rechercher le script contenant les données de variante
        const variantJsonScript = productData.querySelector(
          "script[data-variant-json]",
        );
        if (variantJsonScript) {
          // Stocker les données de variante dans une variable globale
          window.quickViewVariantData = JSON.parse(
            variantJsonScript.textContent,
          );
          console.log(
            "Données de variante extraites:",
            window.quickViewVariantData,
          );

          // Extraire l'ID de variante actuelle
          const currentVariantContainer = productData.querySelector(
            "[data-current-variant-id]",
          );
          if (currentVariantContainer) {
            window.quickViewCurrentVariantId =
              currentVariantContainer.dataset.currentVariantId;
            console.log(
              "ID de variante actuelle:",
              window.quickViewCurrentVariantId,
            );
          }
        }
      } catch (error) {
        console.error(
          "Erreur lors de l'extraction des données de variante:",
          error,
        );
      }

      // Set up variant change listeners
      this.setupVariantChangeListeners(productData);

      // Initialiser le champ de gravure après avoir mis à jour les variantes
      setTimeout(() => {
        this.initQuickViewGravureField(productData);
      }, 300);
    }
  }

  // Fonction pour configurer le gestionnaire d'événements du bouton "Ajouter au panier"
  setupAddToCartHandler(productData) {
    const component = this; // Capturer 'this' pour utilisation dans les callbacks

    // Ajouter un écouteur d'événements pour le bouton "Ajouter au panier"
    const addToCartButton = this.modalContainer.querySelector("[data-add-to-cart]");
    const addToCartButtonforUS = this.modalContainer.querySelector('.product-form__cart-button--us');
    const quickViewData = productData.querySelector('.quick-view-data');
    const showButtonForUS = quickViewData?.getAttribute('data-us-site');
    const redirectionUrlForUS = quickViewData?.getAttribute('data-us-site-url');

    if (showButtonForUS === "true") {
      if (addToCartButton) {
        addToCartButton.style.display = "none";
      }
      if (redirectionUrlForUS) {
        addToCartButtonforUS.href = redirectionUrlForUS;
      } else {
        addToCartButtonforUS.setAttribute('disabled', true);
      }
      addToCartButtonforUS.classList.remove('hidden');
    }

    if (addToCartButton) {
      // Remove any existing event listeners by cloning the button
      const newAddToCartButton = addToCartButton.cloneNode(true);
      addToCartButton.parentNode.replaceChild(newAddToCartButton, addToCartButton);

      newAddToCartButton.addEventListener("click", function (event) {
        // Empêcher la soumission normale du formulaire
        event.preventDefault();
        console.log("Clic sur Ajouter au panier");

        // Check for gravure options if they exist
        const sansGravureRadio = component.modalContainer.querySelector('input[type="radio"][data-option-handle*="sans-gravure"], input[type="radio"][value*="Sans gravure"]');
        const avecGravureRadio = component.modalContainer.querySelector('input[type="radio"][data-option-handle*="avec-gravure"], input[type="radio"][value*="Avec gravure"]');

        // Si l'option "Avec gravure" est sélectionnée, vérifier que le champ est rempli
        if (avecGravureRadio && avecGravureRadio.checked) {
          const gravureInputContainer = component.modalContainer.querySelector(".gravure-input-container");
          const gravureInput = gravureInputContainer ? gravureInputContainer.querySelector(".gravure-input") : null;

          if (gravureInput && gravureInput.value.trim() === "") {
            alert(component.t('please_enter_initials'));
            // Ajouter une animation de secousse et une bordure rouge
            gravureInput.classList.add("error");
            gravureInput.style.border = "1px solid red";
            gravureInput.style.animation = "shake 0.5s";

            // Supprimer les styles d'erreur après un certain temps
            setTimeout(() => {
              gravureInput.classList.remove("error");
              gravureInput.style.border = "";
              gravureInput.style.animation = "";
            }, 2000);

            gravureInput.focus();
            return false;
          }
        }

        // Récupérer l'ID de la variante sélectionnée
        let variantId = null;

        // Méthode 1: Vérifier si la variable globale est définie
        if (window.quickViewCurrentVariantId) {
          variantId = window.quickViewCurrentVariantId;
          console.log(
            "ID de variante trouvé dans la variable globale:",
            variantId,
          );
        }

        // Méthode 2: Vérifier d'abord si un input hidden avec l'ID de variante existe
        if (!variantId) {
          const hiddenVariantInput =
            component.modalContainer.querySelector('input[name="id"]');
          if (hiddenVariantInput && hiddenVariantInput.value) {
            variantId = hiddenVariantInput.value;
            console.log("ID de variante trouvé dans input hidden:", variantId);
          }
        }

        // Méthode 3: Chercher dans le sélecteur de variante
        if (!variantId) {
          const variantSelector = component.modalContainer.querySelector(
            '#quick-view-variant-selector, select[name="id"]',
          );
          if (variantSelector && variantSelector.value) {
            variantId = variantSelector.value;
            console.log("ID de variante trouvé dans le sélecteur:", variantId);
          }
        }

        // Méthode 4: Chercher dans les données JSON du produit
        if (!variantId && window.quickViewVariantData) {
          try {
            // Si nous avons les données de variante JSON, essayer de trouver la variante sélectionnée
            const variantData = window.quickViewVariantData;

            // Si une seule variante existe, utiliser celle-ci
            if (variantData.variants && variantData.variants.length === 1) {
              variantId = variantData.variants[0].id.toString();
              console.log(
                "ID de variante unique trouvé dans les données JSON:",
                variantId,
              );
            }
            // Sinon, essayer de déterminer la variante sélectionnée
            else if (variantData.variants && variantData.variants.length > 1) {
              // Vérifier si nous avons des options sélectionnées
              const selectedOptions = [];
              const optionSelectors = component.modalContainer.querySelectorAll(
                'select[data-option-selector], input[type="radio"][name^="options"]:checked',
              );

              optionSelectors.forEach((selector) => {
                if (selector.tagName === "SELECT") {
                  selectedOptions.push(selector.value);
                } else if (selector.checked) {
                  selectedOptions.push(selector.value);
                }
              });

              console.log("Options sélectionnées:", selectedOptions);

              // Trouver la variante correspondante
              if (selectedOptions.length > 0) {
                const matchingVariant = variantData.variants.find((variant) => {
                  // Vérifier si toutes les options correspondent
                  return selectedOptions.every((option, index) => {
                    return variant[`option${index + 1}`] === option;
                  });
                });

                if (matchingVariant) {
                  variantId = matchingVariant.id.toString();
                  console.log(
                    "ID de variante trouvé par correspondance d'options:",
                    variantId,
                  );
                }
              }
            }
          } catch (error) {
            console.error(
              "Erreur lors de l'analyse des données de variante JSON:",
              error,
            );
          }
        }

        // Méthode 5: Chercher dans les attributs data
        if (!variantId) {
          const variantElements =
            component.modalContainer.querySelectorAll("[data-variant-id]");
          variantElements.forEach((element) => {
            if (
              element.dataset.variantId &&
              (element.classList.contains("selected") ||
                element.checked ||
                element.classList.contains("active"))
            ) {
              variantId = element.dataset.variantId;
              console.log(
                "ID de variante trouvé dans data-variant-id d'un élément sélectionné:",
                variantId,
              );
            }
          });

          // Si aucun élément sélectionné n'a été trouvé, prendre le premier
          if (!variantId && variantElements.length > 0) {
            variantId = variantElements[0].dataset.variantId;
            console.log(
              "ID de variante trouvé dans le premier data-variant-id:",
              variantId,
            );
          }
        }

        // Méthode 6: Chercher dans l'URL du produit
        if (!variantId) {
          const productUrl = component.currentProductUrl;
          const variantMatch = productUrl.match(/variant=(\d+)/);
          if (variantMatch && variantMatch[1]) {
            variantId = variantMatch[1];
            console.log("ID de variante extrait de l'URL:", variantId);
          }
        }

        // Méthode 7: Chercher dans le script JSON de la page
        if (!variantId) {
          try {
            const jsonScripts = component.modalContainer.querySelectorAll(
              'script[type="application/json"]',
            );
            for (let i = 0; i < jsonScripts.length; i++) {
              const scriptContent = jsonScripts[i].textContent;
              if (scriptContent.includes('"variants"')) {
                const jsonData = JSON.parse(scriptContent);
                if (jsonData.variants && jsonData.variants.length > 0) {
                  // Prendre la première variante disponible
                  for (let j = 0; j < jsonData.variants.length; j++) {
                    if (jsonData.variants[j].available) {
                      variantId = jsonData.variants[j].id.toString();
                      console.log(
                        "ID de variante trouvé dans script JSON (première disponible):",
                        variantId,
                      );
                      break;
                    }
                  }

                  // Si aucune variante disponible, prendre la première
                  if (!variantId) {
                    variantId = jsonData.variants[0].id.toString();
                    console.log(
                      "ID de variante trouvé dans script JSON (première):",
                      variantId,
                    );
                  }

                  break;
                }
              }
            }
          } catch (error) {
            console.error("Erreur lors de l'analyse des scripts JSON:", error);
          }
        }

        console.log("ID de variante final:", variantId);

        // Préparer les propriétés personnalisées
        let properties = {};

        if (avecGravureRadio && avecGravureRadio.checked) {
          const gravureInputContainer = component.modalContainer.querySelector(".gravure-input-container");
          const gravureInput = gravureInputContainer ? gravureInputContainer.querySelector(".gravure-input") : null;

          if (gravureInput && gravureInput.value.trim() !== "") {
            // Ajouter la gravure comme propriété personnalisée
            properties["Gravure"] = gravureInput.value.trim();
            console.log("Gravure ajoutée:", gravureInput.value.trim());
          }
        }

        // Ajouter au panier via l'API AJAX de Shopify
        if (variantId) {
          console.log("Tentative d'ajout au panier avec ID:", variantId);

          // Afficher l'état de chargement
          newAddToCartButton.classList.add("loading");
          const addToCartText = newAddToCartButton.querySelector(
            ".ajouter_panier_custom",
          );

          // Préparer les données pour l'ajout au panier
          const cartData = {
            items: [
              {
                id: variantId,
                quantity: 1,
                properties:
                  Object.keys(properties).length > 0 ? properties : undefined,
              },
            ],
          };

          console.log("Données envoyées à l'API:", JSON.stringify(cartData));

          fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(cartData),
          })
            .then((response) => {
              console.log("Réponse reçue, statut:", response.status);
              return response.json();
            })
            .then((data) => {
              console.log("Données reçues:", data);
              // Mettre à jour l'état du bouton
              newAddToCartButton.classList.remove("loading");

              // Emit cart:updated event for the theme's header to update cart count
              // Get updated cart data for the event
              fetch("/cart.js")
                .then((res) => res.json())
                .then((cart) => {
                  // Update cart count manually by finding all cart count elements
                  const cartCountElements = document.querySelectorAll('[data-js-cart-count]');
                  cartCountElements.forEach((element) => {
                    element.textContent = cart.item_count;
                  });

                  // Dispatch custom events if the function is available
                  if (typeof window.dispatchCustomEvent === 'function') {
                    window.dispatchCustomEvent('cart:item-added', {
                      product: data,
                      cart: cart
                    });
                    window.dispatchCustomEvent('cart:updated', {
                      cart: cart
                    });
                  }
                })
                .catch((error) => {
                  console.error("Error fetching cart for events:", error);
                });

              setTimeout(() => {
                fetch("/cart.js")
                  .then((res) => res.json())
                  .then((cart) => {
                    const lastItem = cart.items.find((item) => item.key.startsWith(`${variantId}:`));
                    if (lastItem) {
                      window.refreshAndOpenCartDrawer(lastItem); // Passe le bon produit
                      component.closeModal();
                    } else {
                      console.warn("Produit ajouté non trouvé dans le panier");
                      // En fallback, on passe quand même le dernier item du panier :
                      const fallbackItem = cart.items[0];
                      if (fallbackItem) {
                        window.refreshAndOpenCartDrawer(fallbackItem);
                        component.closeModal();
                      }
                    }
                  });
              }, 400); // Délai de 400ms pour laisser le panier se mettre à jour
            })
            .catch((error) => {
              console.error("Erreur lors de l'ajout au panier:", error);
              newAddToCartButton.classList.remove("loading");
              if (addToCartText) {
                addToCartText.textContent = component.t('error');
              }
              alert(component.t('error_add_to_cart') + ": " + error.message);
            });
        } else {
          console.error(
            "Impossible d'ajouter au panier: ID de variante non trouvé",
          );
          alert(component.t('error_variant_not_found'));
        }
      });
    }
  }

  // Fonction pour configurer les écouteurs d'événements de changement de variante
  setupVariantChangeListeners(productData) {
    const component = this; // Capturer 'this'

    // Get all variant inputs (radio buttons, selects, etc.)
    const variantInputs = this.modalContainer.querySelectorAll(
      'input[name^="options"], select[data-option-index], .dynamic-variant-button'
    );

    if (variantInputs.length === 0) {
      console.log('No variant inputs found');
      return;
    }

    // Add change event listeners to all variant inputs
    variantInputs.forEach(input => {
      input.addEventListener('change', function () {
        console.log('Variant input changed:', this.name, this.value);
        updateSelectedVariant();
      });
    });

    // Function to update the selected variant based on current selections
    function updateSelectedVariant() {
      if (!window.quickViewVariantData || !window.quickViewVariantData.variants) {
        console.log('No variant data available');
        return;
      }

      // Collect all selected option values
      const selectedOptions = [];

      // Handle radio buttons for options
      const radioGroups = component.modalContainer.querySelectorAll('input[name^="options"]:checked');
      radioGroups.forEach(radio => {
        selectedOptions.push(radio.value);
      });

      // Handle select dropdowns
      const selectInputs = component.modalContainer.querySelectorAll('select[data-option-index]');
      selectInputs.forEach(select => {
        if (select.value && select.value !== select.options[0].text) {
          selectedOptions.push(select.value);
        }
      });

      console.log('Selected options:', selectedOptions);

      // Find matching variant
      const matchingVariant = window.quickViewVariantData.variants.find(variant => {
        // Check if this variant matches all selected options
        return selectedOptions.every((option, index) => {
          const optionKey = `option${index + 1}`;
          return variant[optionKey] === option;
        });
      });

      if (matchingVariant) {
        console.log('Matching variant found:', matchingVariant);

        // Update the current variant ID
        window.quickViewCurrentVariantId = matchingVariant.id.toString();

        // Update the hidden input field
        const hiddenVariantInput = component.modalContainer.querySelector('input[name="id"]');
        if (hiddenVariantInput) {
          hiddenVariantInput.value = matchingVariant.id;
        }

        // Update price if available
        updateVariantPrice(matchingVariant);

        // Update availability
        updateVariantAvailability(matchingVariant);

      } else {
        console.warn('No matching variant found for options:', selectedOptions);
      }
    }

    // Function to update price display
    function updateVariantPrice(variant) {
      const priceButtonElement = component.modalContainer.querySelector("[data-product-price-button]");
      if (priceButtonElement && variant.price) {
        let priceHtml = "";

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml += `<s class="btn__compare-price">${formatMoney(variant.compare_at_price)}</s>`;
        }

        priceHtml += `<span class="btn__current-price">${formatMoney(variant.price)}</span>`;
        priceButtonElement.innerHTML = priceHtml;
      }
    }

    // Function to update variant availability
    function updateVariantAvailability(variant) {
      const addToCartButton = component.modalContainer.querySelector("[data-add-to-cart]");
      const addToCartText = addToCartButton ? addToCartButton.querySelector(".ajouter_panier_custom") : null;

      if (addToCartButton && addToCartText) {
        if (variant.available) {
          addToCartButton.disabled = false;
          addToCartButton.classList.remove("btn--disabled");
        } else {
          addToCartButton.disabled = true;
          addToCartButton.classList.add("btn--disabled");
        }
      }
    }

    // Helper function to format money (basic version)
    function formatMoney(cents) {
      if (typeof cents !== 'number') {
        cents = parseInt(cents, 10);
      }

      const amount = (cents / 100).toFixed(2);
      // Basic formatting - you might want to use Shopify's money formatting
      return `${amount} €`;
    }

    // Initialize with current selection
    updateSelectedVariant();
  }

  // Fonction pour initialiser le champ de gravure dans la quick view
  initQuickViewGravureField(productData) {
    console.log("Initializing gravure field...");

    // Sélectionner les boutons radio dans la quick view using more flexible selectors
    const sansGravureRadio = this.modalContainer.querySelector(
      'input[type="radio"][data-option-handle*="sans-gravure"], input[type="radio"][value*="Sans gravure"]',
    );
    const avecGravureRadio = this.modalContainer.querySelector(
      'input[type="radio"][data-option-handle*="avec-gravure"], input[type="radio"][value*="Avec gravure"]',
    );

    console.log("Radio buttons found:", { sansGravureRadio, avecGravureRadio });

    if (!sansGravureRadio || !avecGravureRadio) {
      console.log("Gravure radio buttons not found, exiting...");
      return;
    }

    // Obtenir le texte du placeholder personnalisé depuis les paramètres du thème
    // Par défaut, utiliser "Type your initials..." si le paramètre n'est pas défini
    let placeholderText = "Type your initials...";
    let maxLength = 4; // Valeur par défaut

    // Essayer de récupérer les paramètres depuis la quick view
    const variantPickerBlock = this.modalContainer.querySelector(
      ".product__variants-wrapper, .product__variant-checkboxes",
    );
    if (variantPickerBlock) {
      if (variantPickerBlock.dataset.gravurePlaceholder) {
        placeholderText = variantPickerBlock.dataset.gravurePlaceholder;
      }
      if (variantPickerBlock.dataset.gravureMaxLength) {
        maxLength =
          parseInt(variantPickerBlock.dataset.gravureMaxLength, 10) || 4;
      }
    }

    // Si les paramètres ne sont pas trouvés dans la quick view, essayer de les récupérer depuis la page produit
    if (
      !variantPickerBlock ||
      (!variantPickerBlock.dataset.gravurePlaceholder &&
        !variantPickerBlock.dataset.gravureMaxLength)
    ) {
      // Essayer de récupérer les paramètres depuis la page produit via le template
      const productVariantData = productData.querySelector(
        "[data-gravure-placeholder], [data-gravure-max-length]",
      );
      if (productVariantData) {
        if (productVariantData.dataset.gravurePlaceholder) {
          placeholderText = productVariantData.dataset.gravurePlaceholder;
        }
        if (productVariantData.dataset.gravureMaxLength) {
          maxLength =
            parseInt(productVariantData.dataset.gravureMaxLength, 10) || 4;
        }
      }
    }

    // Créer le conteneur de gravure
    const gravureInputContainer = document.createElement("div");
    gravureInputContainer.className = "gravure-input-container";
    gravureInputContainer.style.display = avecGravureRadio.checked
      ? "block"
      : "none";
    gravureInputContainer.style.marginTop = "10px";

    // Créer le champ de saisie pour la gravure
    const gravureInput = document.createElement("input");
    gravureInput.type = "text";
    gravureInput.id = "gravure-text";
    gravureInput.className = "gravure-input !border-none !border-0";
    gravureInput.placeholder = placeholderText;
    gravureInput.maxLength = maxLength;

    // Ajouter le champ au conteneur
    gravureInputContainer.appendChild(gravureInput);

    // Try multiple possible containers for inserting the gravure input
    let targetContainer = null;

    // First try: Look for product__variant-liste (this should work based on the HTML you shared)
    targetContainer = this.modalContainer.querySelector(".product__variant-liste");

    // Second try: Look for the parent of the radio buttons
    if (!targetContainer && avecGravureRadio.parentNode) {
      targetContainer = avecGravureRadio.parentNode;
    }

    // Third try: Look for the variants container
    if (!targetContainer) {
      targetContainer = this.modalContainer.querySelector("[data-product-variants]");
    }

    // Fourth try: Look for the quick-view-gravure-field placeholder
    if (!targetContainer) {
      targetContainer = this.modalContainer.querySelector(".quick-view-gravure-field");
    }

    console.log("Target container found:", targetContainer);

    if (targetContainer) {
      targetContainer.appendChild(gravureInputContainer);
      console.log("Gravure input container added to:", targetContainer.className);
    } else {
      console.error("No suitable container found for gravure input");
    }

    // Ajouter les écouteurs d'événements pour les boutons radio
    sansGravureRadio.addEventListener("change", function () {
      console.log("Sans gravure selected");
      if (this.checked) {
        gravureInputContainer.style.display = "none";
      }
    });

    avecGravureRadio.addEventListener("change", function () {
      console.log("Avec gravure selected");
      if (this.checked) {
        gravureInputContainer.style.display = "block";
        const gravureInput =
          gravureInputContainer.querySelector(".gravure-input");
        if (gravureInput) {
          gravureInput.focus();
        }
      }
    });
  }

  // Fonction pour créer la modal
  createModal() {
    // Vérifier si la modal existe déjà
    if (document.getElementById("new-quick-view-overlay")) {
      return;
    }

    // Récupérer le template depuis le DOM
    const template = this.querySelector('#new-quick-view-template');

    if (!template) {
      console.error('Template #new-quick-view-template non trouvé dans <new-quick-view>');
      return;
    }

    // Cloner le contenu du template
    const templateContent = template.content.cloneNode(true);

    // Récupérer l'overlay et le container depuis le template cloné
    this.modalOverlay = templateContent.querySelector('.new-quick-view-overlay');
    this.modalContainer = templateContent.querySelector('.new-quick-view-modal');

    // Ajouter la modal au DOM
    document.body.appendChild(templateContent);

    // Ajouter les écouteurs d'événements pour fermer la modal
    const closeButton = this.modalContainer.querySelector(".new-quick-view-close");
    if (closeButton) {
      closeButton.addEventListener("click", this.closeModal);
    }

    this.modalOverlay.addEventListener("click", (event) => {
      if (event.target === this.modalOverlay) {
        this.closeModal();
      }
    });

    // Ajouter un écouteur pour la touche Escape
    document.addEventListener("keydown", this.handleEscapeKey);
  }

  // Fonction pour initialiser le lecteur audio
  initAudioPlayer() {
    const audioPlayButton = this.modalContainer.querySelector("[data-audio-play]");
    if (!audioPlayButton) return;

    const video = this.modalContainer.querySelector("#quick-view-product-audio");
    const timeDisplay = this.modalContainer.querySelector("[data-audio-time]");

    if (!video || !timeDisplay) return;

    // Réinitialiser l'état du lecteur
    video.pause();
    video.currentTime = 0;
    timeDisplay.textContent = "00:00";

    // Mettre à jour l'affichage du temps
    function updateTimeDisplay() {
      const minutes = Math.floor(video.currentTime / 60);
      const seconds = Math.floor(video.currentTime % 60);
      timeDisplay.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    // Supprimer les écouteurs d'événements existants pour éviter les doublons
    const newAudioPlayButton = audioPlayButton.cloneNode(true);
    audioPlayButton.parentNode.replaceChild(
      newAudioPlayButton,
      audioPlayButton,
    );

    // Gérer le clic sur le bouton de lecture
    newAudioPlayButton.addEventListener("click", function () {
      if (video.paused) {
        // Arrêter tous les autres lecteurs audio
        document.querySelectorAll("video").forEach((v) => {
          if (v !== video) {
            v.pause();
          }
        });

        // Lire la vidéo
        video
          .play()
          .then(() => {
            // Ajouter la classe playing une fois que la lecture a commencé
            newAudioPlayButton.classList.add("playing");
          })
          .catch((error) => {
            console.error("Erreur lors de la lecture de la vidéo:", error);
          });
      } else {
        // Mettre en pause
        video.pause();
        newAudioPlayButton.classList.remove("playing");
      }
    });

    // Mettre à jour l'affichage du temps pendant la lecture
    video.addEventListener("timeupdate", updateTimeDisplay);

    // Gérer la fin de la lecture
    video.addEventListener("ended", function () {
      newAudioPlayButton.classList.remove("playing");
      video.currentTime = 0;
      updateTimeDisplay();
    });

    // Gérer les événements de lecture et de pause
    video.addEventListener("play", function () {
      newAudioPlayButton.classList.add("playing");
    });

    video.addEventListener("pause", function () {
      newAudioPlayButton.classList.remove("playing");
    });
  }

  // Initialisation : créer la modal
  init() {
    this.createModal();
  }
}

customElements.define("new-quick-view", NewQuickView);